import { array, number, object, string, mixed, bool } from 'yup';
import { useIntl } from 'react-intl';
import { useMemo } from 'react';
import { v4 } from 'uuid';
import { BILLING_COLUMN_TYPE } from '~/modules/common/enums';
import { useMeContext } from '~/modules/me/useMeContext';
import { useUpdateProjectBillingSetting } from './useUpdateProjectBillingSetting';

const getInvoiceCurrency = invoiceCurrency => {
  return invoiceCurrency
    ? {
        __typename: 'Currency',
        id: invoiceCurrency.id,
        symbol: invoiceCurrency.symbol
      }
    : null;
};

const getFilteredBillLineItemOptions = (
  billLineItemsBy,
  isPsaFPOvertimeBillingEnabled,
  isPsaPrpInvoiceBillingRateEnabled
) =>
  billLineItemsBy?.filter(columnOption => {
    if (columnOption === BILLING_COLUMN_TYPE.EXPENSE_CODE) {
      return true;
    }
    if (
      columnOption === BILLING_COLUMN_TYPE.PAYCODE ||
      columnOption === BILLING_COLUMN_TYPE.ROLE
    ) {
      return isPsaFPOvertimeBillingEnabled;
    }
    if (columnOption === BILLING_COLUMN_TYPE.BILLING_RATE) {
      return isPsaPrpInvoiceBillingRateEnabled;
    }

    return true;
  });

const mapToBillingSettings = ({
  context,
  defaultBillSettings,
  projectBillSettings,
  isPsaFPOvertimeBillingEnabled,
  isPsaPrpInvoiceBillingRateEnabled,
  baseCurrency
}) => {
  const {
    billLineItemsBy,
    description,
    internalNotes,
    showComments,
    showExpenseReceipts,
    defaultInvoicePaymentTerm,
    invoiceCurrency,
    invoiceTemplate,
    taxProfile
  } = (context === 'Project' ? projectBillSettings : defaultBillSettings) ?? {};
  const filteredBillLineItemOptions = getFilteredBillLineItemOptions(
    billLineItemsBy,
    isPsaFPOvertimeBillingEnabled,
    isPsaPrpInvoiceBillingRateEnabled
  );

  return {
    defaultInvoicePaymentTerm: defaultInvoicePaymentTerm || 30,
    billLineItemsBy: filteredBillLineItemOptions || [
      BILLING_COLUMN_TYPE.PROJECT
    ],
    invoiceCurrency:
      getInvoiceCurrency(invoiceCurrency) || getInvoiceCurrency(baseCurrency),
    taxProfile,
    invoiceTemplate: invoiceTemplate || null,
    description: description || '',
    internalNotes: internalNotes || '',
    showComments: showComments || false,
    showExpenseReceipts: Boolean(showExpenseReceipts),
    poNumber: projectBillSettings?.poNumber || ''
  };
};

export const initialState = ({
  id,
  defaultBillSettings,
  projectBillSettings,
  isPsaFPOvertimeBillingEnabled,
  clientDefaultData,
  isPsaPrpInvoiceBillingRateEnabled,
  showConfirmationDialogBeforeSave,
  baseCurrency
}) => {
  const effectiveSettings = mapToBillingSettings({
    context: projectBillSettings?.billLineItemsBy ? 'Project' : 'Client',
    defaultBillSettings,
    projectBillSettings,
    isPsaFPOvertimeBillingEnabled,
    isPsaPrpInvoiceBillingRateEnabled,
    baseCurrency
  });

  return {
    key: v4(),
    id,
    ...effectiveSettings,
    context: 'project',
    showConfirmationDialogBeforeSave
  };
};

export const buildValidationSchema = ({ intl }) =>
  object().shape({
    id: string(),
    invoiceCurrency: string().nullable(),
    invoiceTemplate: string().nullable(),
    defaultInvoicePaymentTerm: number()
      .min(0)
      .max(25000)
      .nullable(),
    taxProfile: string().nullable(),
    billLineItemsBy: array().of(
      mixed().oneOf(Object.keys(BILLING_COLUMN_TYPE))
    ),
    description: string()
      .max(
        255,
        intl.formatMessage({
          id: 'billDetails.validations.descriptionExceedsMaxLength'
        })
      )
      .nullable(),
    internalNotes: string().nullable(),
    showComments: bool(),
    showExpenseReceipts: bool()
  });

const useFormState = ({
  id,
  defaultBillSettings,
  projectBillSettings,
  clientDefaultData,
  showConfirmationDialogBeforeSave
}) => {
  const intl = useIntl();
  const {
    featureFlags: {
      isPsaFPOvertimeBillingEnabled,
      isPsaPrpInvoiceBillingRateEnabled
    },
    baseCurrency
  } = useMeContext();
  const initialValues = initialState({
    id,
    defaultBillSettings,
    projectBillSettings,
    isPsaFPOvertimeBillingEnabled,
    clientDefaultData,
    isPsaPrpInvoiceBillingRateEnabled,
    showConfirmationDialogBeforeSave,
    baseCurrency
  });

  const validationSchema = useMemo(() => buildValidationSchema({ intl }), [
    intl
  ]);

  const { onSubmit } = useUpdateProjectBillingSetting({
    initialValues,
    projectBillSettings
  });

  return {
    onSubmit,
    initialValues,
    validationSchema,
    enableReinitialize: true
  };
};

export default useFormState;
